import { useReducer, useState, useEffect, useMemo } from 'react'
import Big from 'big.js'

import calcFees from 'src/utils/calcFees'
import prettyAmount from 'src/utils/prettyAmount'
import { nextTipOption } from 'src/utils/tipOptions'
import useValidation from './useValidation'

const calcTip = (bigAmount, currency, { otherTip, tipOption, tipText }) => {
  const bigTipOption = Big(tipOption || 0)
  return otherTip
  ? Big(tipText || 0).toFixed(currency.exponent)
  : bigTipOption.gte(1)
  ? bigTipOption.toFixed(currency.exponent)
  : bigAmount.times(bigTipOption).round(currency.exponent, 0).toFixed(currency.exponent)
}

const findTippingConfig = ({ tipping, amount }) => {
  const bigAmount = Big(amount || 0)
  return tipping.auto
  && tipping.configs.find(config => bigAmount.gte(config.threshold))
  || tipping.fixed
}

const reducer = (state, action) => {
  switch (action.type) {
  case 'setCurrency': {
    const { value, presets } = action
    // Carry the preset selection across currency changes by index —
    // when switching from USD to EUR with the $50 preset selected, the
    // donor lands on the EUR preset at the same position. Falls through
    // to the last preset if the new currency has fewer.
    const carry = state.presetIndex > -1
      ? presets[state.presetIndex]?.amount || presets.at(-1)?.amount
      : state.amount
    return {
      ...state,
      currency: value,
      amount: carry,
    }
  }
  case 'setAmount': {
    const { amount, presetIndex } = action
    return { ...state, amount, presetIndex }
  }
  case 'toggleOtherTip': {
    const otherTip = !state.otherTip
    const tipText = otherTip
    ? calcTip(Big(state.amount || 0), action.currency, { tipOption: state.tipOption })
    : state.tipText
    return { ...state, otherTip, tipText }
  }
  case 'resetTipping':
    return { ...state, tipOption: nextTipOption({
      declined: state.tipDeclined,
      config: action.tippingConfig,
      zeroTip: action.zeroTip,
    }) }
  case 'setTipOption':
    return {
      ...state,
      tipOption: action.value,
      otherTip: false,
      // Only a pick from the menu counts as declining — see nextTipOption.
      tipDeclined: Big(action.value || 0).eq(0),
    }
  case 'setTipText':
    return { ...state, tipText: action.value }
  case 'setCoverCosts':
    return { ...state, coverCosts: action.value }
  case 'setFieldValue':
    return { ...state, [action.field]: action.value }
  case 'setAddress':
    return { ...state, ...action.value }
  case 'setSubscription':
    return { ...state, subscription: action.value }
  default:
    throw new Error('Invalid reducer action')
  }
}

const useDonation = ({
  i18n,
  amount, currencies, presets: presetsByCurrency, min_amount, blank_amount,
  order, tipping, collect_address, feeConfig, canadianReceipt, giftAid,
  subscription,
}) => {
  const { tippingEnabled, coverCostsEnabled, zeroTip } = tipping

  const [ state, dispatch ] = useReducer(reducer, amount, originalAmount => {
    const amount = blank_amount ? '' : prettyAmount(originalAmount)
    return {
      currency: order.currency,
      amount,
      presetIndex: (presetsByCurrency[order.currency] || [])
        .findIndex(p => p.amount === amount),
      coverCosts: coverCostsEnabled,
      tipOption: tippingEnabled ? findTippingConfig({ tipping, amount })?.default : undefined,
      tipText: '',
      otherTip: false,
      tipDeclined: false,

      firstName:    order.user_first_name   || '',
      lastName:     order.user_last_name    || '',
      email:        order.email             || '',
      phone:        order.user_phone_number || '',
      subscription,
    }
  })
  const [ shares, setShares ] = useState(order.shares)

  // Defensive fallback chain: a state.currency that doesn't match
  // anything in `currencies` (e.g. a setCurrency dispatch with a stale
  // / typo'd value) would otherwise return undefined and crash the
  // widget on the next line (`currency.rate.round(...)`). Fall back to
  // the order's initial currency, then to the first configured
  // currency, so the widget keeps rendering even if state diverges
  // from the configured set.
  const currency = useMemo(() =>
    currencies.find(currency => currency.id == state.currency)
      || currencies.find(currency => currency.id == order.currency)
      || currencies[0]
  , [ state.currency ])
  const minAmount = useMemo(() =>
    Big(min_amount).times(currency.rate).round(currency.exponent, 3)
  , [ min_amount, currency ])
  const tippingConfig = useMemo(() =>
    tippingEnabled
      ? findTippingConfig({ tipping, amount: Math.max(state.amount, minAmount) })
      : null
  , [ tipping, tippingEnabled, state.amount ])

  useEffect(() => {
    if (tippingConfig)
      dispatch({ type: 'resetTipping', tippingConfig, zeroTip })
  }, [ tippingConfig ])

  // The active currency's preset list — array of `{ amount, impact?, emoji? }`.
  // Consumers (Amount.jsx, OrderSummary.jsx) iterate this and read `.amount`
  // to render labels and to detect which preset the donor has selected for
  // impact display.
  const presets = presetsByCurrency[state.currency] || []
  const bigAmount = Big(state.amount || 0)

  const fees = coverCostsEnabled
    ? calcFees(bigAmount, currency, feeConfig, tippingEnabled)
    : null
  const tip = tippingEnabled ? calcTip(bigAmount, currency, state) : null
  // The donor's gift, normalized to the currency's scale — what they chose to
  // give, never including a covered fee or a tip. Distinct from donationAmount
  // and total below. `state.amount` is raw donor input and isn't reliably
  // scaled, so don't substitute it. Reported to the embedding page as `amount`;
  // see utils/messages/completedDonation.js.
  const giftAmount = bigAmount.toFixed(currency.exponent)

  // What gets persisted as order.donation_amount: the donor's gift plus
  // any covered fees, but NOT the tip — tip rides separately in
  // tip_amount and the backend re-sums to derive the charged total.
  // Distinct from `total` below, which DOES include the tip and is what
  // Stripe / PayPal / wallet sheets charge.
  const donationAmount = coverCostsEnabled && state.coverCosts
    ? bigAmount.plus(fees).toFixed(currency.exponent)
    : bigAmount.toFixed(currency.exponent)
  const total = tippingEnabled
    ? Big(donationAmount || 0).plus(tip).toFixed(currency.exponent)
    : donationAmount

  const usdAmount = bigAmount.div(currency.rate)
  const collectAddress = collect_address
  || usdAmount.gte(5000)
  || canadianReceipt?.value
  || giftAid?.value

  // Tip cap: donor's tip can't exceed max(donation amount, largest
  // configured tip option). Mirrors v6 (donate_v6/fiat/useValidations.js)
  // — fixed-amount presets above the donation amount remain selectable,
  // but a free-form Other tip can't be larger than what the donor is
  // donating. Auto-clears as `tip` changes so a fix to either tip or
  // donation amount (recomputed via calcTip) drops the error.
  const tipValidation = useValidation({
    i18n,
    currency,
    fieldSet: {
      tip: {
        max: ({ state, tippingConfig }) => ({
          value: Math.max(state.amount, tippingConfig.options.at(-1)),
          message: 'more_than_donation',
        }),
      },
    },
  })

  useEffect(() => {
    tipValidation.resetErrorsFor('tip')
  }, [ tip ])

  // No-op (returns valid) when tipping isn't enabled or there's no
  // tippingConfig yet — the fieldSet rule would crash trying to read
  // `tippingConfig.options.at(-1)` otherwise.
  const validateTip = () =>
    tippingEnabled && tippingConfig
      ? tipValidation.validate({ tip, state, tippingConfig })
      : true

  const setCurrency = value =>
    dispatch({ type: 'setCurrency', presets: presetsByCurrency[value] || [], value })

  const setAmount = (amount, presetIndex = -1) =>
    dispatch({ type: 'setAmount', amount, presetIndex })

  const toggleOtherTip = () => dispatch({ type: 'toggleOtherTip', currency })

  const setSubscription = value => dispatch({ type: 'setSubscription', value })

  return {
    state, dispatch, currency, presets, fees, tip, donationAmount, total,
    giftAmount,
    bigAmount, minAmount, collectAddress, setCurrency, setAmount,
    toggleOtherTip, setSubscription, shares, setShares, tippingConfig,
    usdAmount,
    // Rides on `donation` rather than a <Tipping> prop — it would
    // otherwise have to thread through all four payment steps that mount
    // the component. NOT folded into tippingConfig: that object's
    // identity drives the resetTipping effect, and respreading it would
    // clear the donor's tip choice on every amount keystroke.
    zeroTip,
    tipError: tipValidation.errors.tip?.[0],
    validateTip,
  }
}

export default useDonation
